import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const INQUIRY_TYPES = ['General Inquiry', 'Membership Question', 'Technical Support', 'Property Inquiry', 'Partnership Opportunity', 'Press & Media'];

const CONTACT_INFO = [
  {
    icon: 'ri-mail-send-line',
    title: 'Email Us',
    value: 'support@foreclosurerecovery.net',
    sub: 'Typically responds within 2-4 hours',
  },
  {
    icon: 'ri-phone-line',
    title: 'Call Us',
    value: '(855) 467-3226',
    sub: 'Mon–Fri, 8am–6pm PT',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Our Office',
    value: '1234 Wilshire Blvd, Suite 800',
    sub: 'Los Angeles, CA 90010',
  },
  {
    icon: 'ri-time-line',
    title: 'Office Hours',
    value: 'Monday – Friday',
    sub: '8:00 AM – 6:00 PM Pacific Time',
  },
];

const FAQS = [
  { q: 'How quickly can I access properties after joining?', a: 'Immediately! Once your membership is active you get instant access to our full property database, including pre-market listings available 30–90 days before public auction.' },
  { q: 'Do you offer a refund if I\'m not satisfied?', a: 'Yes. We offer a 30-day money-back guarantee on all membership plans. If you\'re not completely satisfied, contact us within 30 days for a full refund, no questions asked.' },
  { q: 'Is the 14-day trial really free?', a: 'Absolutely. The 14-day free trial gives you full access to your chosen membership tier. No credit card is required to start, and you won\'t be charged unless you decide to continue.' },
  { q: 'What states do you currently cover?', a: 'We currently cover California, Nevada, and Arizona with a combined database of 2,847+ active listings. We are actively expanding to other Southwest states.' },
  { q: 'Can I upgrade or downgrade my membership?', a: 'Yes, you can change your membership tier at any time. Upgrades take effect immediately, and downgrades take effect at the start of your next billing cycle.' },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: INQUIRY_TYPES[0],
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));
      await fetch('https://readdy.ai/api/form/d75cicavu4krcbn3bka0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-navy pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/05b56952202584d769caabdf95e508b8.jpg"
              alt="Contact Us"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 border border-gold/50 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Get In Touch
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-6">Contact Us</h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto">
              Have questions about membership, properties, or our services? Our expert team is ready to help.
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="bg-cream py-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {CONTACT_INFO.map((info) => (
                <div key={info.title} className="bg-white rounded-xl p-6 border border-gray-100 flex flex-col gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10">
                    <i className={`${info.icon} text-gold text-xl`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{info.title}</p>
                    <p className="text-navy font-semibold text-sm">{info.value}</p>
                    <p className="text-gray-400 text-xs mt-1">{info.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Section: Form + Map */}
        <section className="bg-white py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              {/* Form */}
              <div className="lg:col-span-3">
                <h2 className="font-playfair text-3xl font-bold text-navy mb-2">Send Us a Message</h2>
                <p className="text-gray-500 text-sm mb-8">Typical response time: 2–4 business hours</p>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-3xl" />
                    </div>
                    <h3 className="font-playfair text-2xl font-bold text-navy mb-2">Message Sent!</h3>
                    <p className="text-gray-500">Thank you for reaching out. Our team will respond within 2–4 business hours.</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 000-0000"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Inquiry Type *
                        </label>
                        <select
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700 bg-white cursor-pointer"
                        >
                          {INQUIRY_TYPES.map((t) => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="What is your question about?"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Message *
                        </label>
                        <span className={`text-xs ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                          {charCount}/500
                        </span>
                      </div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="Tell us how we can help you..."
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700 resize-none"
                      />
                    </div>

                    {error && (
                      <p className="text-red-500 text-sm">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex items-center justify-center gap-2 w-full py-4 bg-navy text-white font-bold rounded-lg hover:bg-navy-light transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          Send Message <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Map + Sidebar */}
              <div className="lg:col-span-2 flex flex-col gap-8">
                {/* Map */}
                <div>
                  <h3 className="font-playfair text-xl font-bold text-navy mb-4">Our Location</h3>
                  <div className="rounded-xl overflow-hidden h-64 border border-gray-100">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3306.2738697027434!2d-118.40853608478893!3d34.05850238060697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2bb1ae6f10f35%3A0x4f98fa7d3b7b24db!2s1234%20Wilshire%20Blvd%2C%20Los%20Angeles%2C%20CA%2090010!5e0!3m2!1sen!2sus!4v1690000000000!5m2!1sen!2sus"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Foreclosure Recovery Office Location"
                      aria-label="Map showing Foreclosure Recovery office at 1234 Wilshire Blvd, Los Angeles"
                    />
                  </div>
                </div>

                {/* Response Time */}
                <div className="bg-cream rounded-xl p-6 border border-gray-100">
                  <h4 className="font-semibold text-navy mb-4">Quick Response Commitment</h4>
                  <div className="flex flex-col gap-3">
                    {[
                      { type: 'Membership Questions', time: '&lt; 2 Hours', color: 'text-green-600' },
                      { type: 'Technical Support', time: '&lt; 4 Hours', color: 'text-green-600' },
                      { type: 'Property Inquiries', time: '&lt; 6 Hours', color: 'text-amber-600' },
                      { type: 'General Questions', time: '&lt; 24 Hours', color: 'text-amber-600' },
                    ].map((item) => (
                      <div key={item.type} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{item.type}</span>
                        <span className={`text-xs font-bold ${item.color}`} dangerouslySetInnerHTML={{ __html: item.time }} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social */}
                <div>
                  <h4 className="font-semibold text-navy mb-4">Follow Us</h4>
                  <div className="flex gap-3">
                    {[
                      { icon: 'ri-linkedin-box-line', label: 'LinkedIn' },
                      { icon: 'ri-twitter-x-line', label: 'Twitter' },
                      { icon: 'ri-facebook-box-line', label: 'Facebook' },
                      { icon: 'ri-youtube-line', label: 'YouTube' },
                    ].map((social) => (
                      <a
                        key={social.label}
                        href="#"
                        rel="nofollow noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-navy hover:text-navy transition-colors"
                        aria-label={social.label}
                      >
                        <i className={`${social.icon} text-lg`} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-cream py-20">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-4xl font-bold text-navy">Frequently Asked Questions</h2>
            </div>
            <div className="flex flex-col gap-3">
              {FAQS.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-navy text-sm pr-4">{faq.q}</span>
                    <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-gold text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5 border-t border-gray-50">
                      <p className="text-gray-500 text-sm leading-relaxed pt-4">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
